import {ReactElement, useContext, useEffect, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./styles/selectionBar.module.scss";
import {CSVBoxButton} from "../../../components/csvbox/CSVBoxButton";
import {AuthContext} from "../../../contexts/AuthContext";
import {csvBoxExtractRows, csvBoxGeneralError} from "../../../global/csv_box_helpers";
import {
    useGenerateAcquisitionMarketRentScheduleFileUploadUrlMutation,
    useProcessAcquisitionMarketRentScheduleFileMutation
} from "../../../__generated__/generated_types";
import {toast} from "react-toastify";

interface AcquisitionMRSDataItem {
    unitType: string;
    unitCount: string;
    occupiedUnitCount: string;
    squareFeet: string;
    marketRent: string;
    residentRent: string;
}

function parseNumberField(raw: unknown): string | null  {
    if(typeof raw === "number") {
        return raw.toString();
    } else if(typeof raw === "string") {
        if(raw === "" || isNaN(parseFloat(raw))) {
            return null;
        }
        return raw;
    } else {
        return null;
    }
}

function extractData(values: unknown[]): AcquisitionMRSDataItem[] {
    const retVals: AcquisitionMRSDataItem[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;

        const unitType = row["Unit Type"];
        const unitCountRaw = row["Unit Count"];
        const occupiedUnitCountRaw = row["Occupied Unit Count"];
        const squareFeetRaw = row["Square Feet"];
        const marketRentRaw = row["Market Rent"];
        const residentRentRaw = row["Resident Rent"];

        if(typeof unitType !== "string" || unitType.trim() === "") {
            continue;
        }

        const unitCount = parseNumberField(unitCountRaw);
        if(unitCount === null) {
            continue;
        }

        const occupiedUnitCount = parseNumberField(occupiedUnitCountRaw);
        if(occupiedUnitCount === null) {
            continue;
        }

        const squareFeet = parseNumberField(squareFeetRaw);
        if(squareFeet === null) {
            continue;
        }

        const marketRent = parseNumberField(marketRentRaw);
        if(marketRent === null) {
            continue;
        }

        const residentRent = parseNumberField(residentRentRaw);
        if(residentRent === null) {
            continue;
        }

        retVals.push({
            unitType: unitType,
            unitCount: unitCount,
            occupiedUnitCount: occupiedUnitCount,
            squareFeet: squareFeet,
            marketRent: marketRent,
            residentRent: residentRent
        });
    }

    return retVals;
}

export interface AcquisitionPropertyMRSButtonProps {
    propertyId: string;
    jobCreated: (jobId: string, propertyId: string) => void;
}

export function AcquisitionPropertyMRSButton(props: AcquisitionPropertyMRSButtonProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [generateUrl, {loading: loadingUrl, data: urlData}] = useGenerateAcquisitionMarketRentScheduleFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processMrs, {loading: loadingProcess}] = useProcessAcquisitionMarketRentScheduleFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const [data, setData] = useState<AcquisitionMRSDataItem[] | null>(null);

    useEffect(() => {
        if(loadingUrl || !urlData) {
            return;
        }

        doUpload();
    }, [loadingUrl, urlData]);

    function doUpload(): void {
        if(!data) {
            return;
        }

        if(!urlData) {
            toast.error("Unable to determine file location.");
            return;
        }

        const mrsUrl = urlData.generateAcquisitionMarketRentScheduleFileUploadUrl.signedUrl;
        const mrsKey = urlData.generateAcquisitionMarketRentScheduleFileUploadUrl.key;

        const encoder = new TextEncoder();
        const encoded = encoder.encode(JSON.stringify(data));

        fetch(mrsUrl, {
            method: 'PUT',
            body: encoded.buffer,
            headers: {
                'Content-Type': "application/json",
            }
        })
        .then(() => {
            return processMrs({
                variables: {
                    propertyId: props.propertyId,
                    fileKey: mrsKey
                }
            });
        })
        .then(res => {
            if(res.data?.processAcquisitionMarketRentScheduleFile) {
                props.jobCreated(res.data.processAcquisitionMarketRentScheduleFile.id, props.propertyId);
                toast.success("Market rent schedule uploaded successfully.");
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        });
    }

    function handleSave(data: unknown): void {
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("market rent schedule");
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("market rent schedule");
            return;
        }

        setData(extracted);
        generateUrl({
            variables: {
                propertyId: props.propertyId
            }
        });
    }

    return <CSVBoxButton
        licenseKey="RjSDb9CJzzr6dRJu2dclu134X1bYca"
        user={{
            userId: user?.id ?? "unknown",
            name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
            email: user?.email ?? "unknown",
            clientId: user?.clientId ?? "unknown",
            clientName: user?.clientName ?? "unknown",
        }}
        onImport={(result, data) => {
            if(result) {
                handleSave(data);
            } else {
                csvBoxGeneralError("market rent schedule");
            }
        }}
        render={(launch, isLoading) => {
            return <Button className={css.userSelectionButton} onClick={launch} disabled={isLoading || loadingUrl || loadingProcess}>Update MRS</Button>;
        }}
    />;
}